import {
  Team,
  Match,
  Round,
  Tournament,
  GameResult,
  TournamentStats,
  PathToCourt1,
} from "../types/tournament";

// Generate a unique ID
export const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

// Create a new team
export const createTeam = (
  name: string,
  player1: string,
  player2: string
): Team => {
  return {
    id: generateId(),
    name,
    player1,
    player2,
    wins: 0,
    losses: 0,
    currentCourt: 0,
    startingCourt: 0,
    totalMovement: 0,
    achievements: [],
    court1Streak: 0,
    perfectGames: 0,
  };
};

// Determine winner based on pickleball scoring rules
export const determineWinner = (
  team1Score: number,
  team2Score: number
): { winner: number; loser: number } => {
  // Games to 11, win by 2, cap at 13
  if (team1Score >= 11 && team1Score - team2Score >= 2) {
    return { winner: 1, loser: 2 };
  }
  if (team2Score >= 11 && team2Score - team1Score >= 2) {
    return { winner: 2, loser: 1 };
  }
  if (team1Score === 13) {
    return { winner: 1, loser: 2 };
  }
  if (team2Score === 13) {
    return { winner: 2, loser: 1 };
  }
  return { winner: 0, loser: 0 }; // Game not finished
};

// Create game result
export const createGameResult = (
  team1Score: number,
  team2Score: number,
  team1Id: string,
  team2Id: string
): GameResult => {
  const { winner, loser } = determineWinner(team1Score, team2Score);
  const isPerfectGame =
    (winner === 1 && team1Score === 11 && team2Score === 0) ||
    (winner === 2 && team2Score === 11 && team1Score === 0);
  const isCloseMatch = Math.abs(team1Score - team2Score) <= 2;

  return {
    team1Score,
    team2Score,
    winner: winner === 1 ? team1Id : winner === 2 ? team2Id : "",
    loser: loser === 1 ? team1Id : loser === 2 ? team2Id : "",
    completed: winner !== 0,
    isPerfectGame,
    isCloseMatch,
  };
};

// Check and award achievements
export const checkAchievements = (
  teams: Team[],
  result: GameResult
): Team[] => {
  return teams.map((team) => {
    const newAchievements = [...team.achievements];

    // Perfect Game achievement
    if (
      result.isPerfectGame &&
      ((result.winner === team.id &&
        result.team1Score === 11 &&
        result.team2Score === 0) ||
        (result.winner === team.id &&
          result.team2Score === 11 &&
          result.team1Score === 0))
    ) {
      const hasPerfectGame = team.achievements.some(
        (a) => a.type === "perfect_game"
      );
      if (!hasPerfectGame) {
        newAchievements.push({
          id: generateId(),
          name: "Perfect Game",
          description: "Win a match 11-0",
          icon: "🏆",
          earnedAt: new Date(),
          type: "perfect_game",
        });
      }
    }

    // Iron Wall achievement (stay on Court 1 for 3+ rounds)
    if (team.currentCourt === 1 && team.court1Streak >= 3) {
      const hasIronWall = team.achievements.some((a) => a.type === "iron_wall");
      if (!hasIronWall) {
        newAchievements.push({
          id: generateId(),
          name: "Iron Wall",
          description: "Stay on Court 1 for 3+ rounds",
          icon: "🛡️",
          earnedAt: new Date(),
          type: "iron_wall",
        });
      }
    }

    return {
      ...team,
      achievements: newAchievements,
      perfectGames:
        team.perfectGames +
        (result.isPerfectGame && result.winner === team.id ? 1 : 0),
    };
  });
};

// Update team stats after a game
export const updateTeamStats = (teams: Team[], result: GameResult): Team[] => {
  let updatedTeams = teams.map((team) => {
    if (team.id === result.winner) {
      return { ...team, wins: team.wins + 1 };
    }
    if (team.id === result.loser) {
      return { ...team, losses: team.losses + 1 };
    }
    return team;
  });

  // Check achievements
  updatedTeams = checkAchievements(updatedTeams, result);

  return updatedTeams;
};

// Update court 1 streak for teams
export const updateCourt1Streak = (teams: Team[]): Team[] => {
  return teams.map((team) => {
    if (team.currentCourt === 1) {
      return { ...team, court1Streak: team.court1Streak + 1 };
    } else {
      return { ...team, court1Streak: 0 };
    }
  });
};

// Find match of the round (closest score)
export const findMatchOfTheRound = (matches: Match[]): Match | null => {
  const completedMatches = matches.filter(
    (m) => m.result && m.result.completed && !m.isBye
  );

  if (completedMatches.length === 0) return null;

  return completedMatches.reduce((closest, match) => {
    if (!match.result) return closest;

    const currentDiff = Math.abs(
      match.result.team1Score - match.result.team2Score
    );
    const closestDiff = closest?.result
      ? Math.abs(closest.result.team1Score - closest.result.team2Score)
      : Infinity;

    return currentDiff < closestDiff ? match : closest;
  }, null as Match | null);
};

// Calculate path to Court 1 for a team
export const calculatePathToCourt1 = (
  team: Team,
  allTeams: Team[]
): PathToCourt1 => {
  const currentPosition = team.currentCourt;
  const gamesNeeded = currentPosition - 1;

  // Find teams currently above this team
  const teamsAbove = allTeams
    .filter((t) => t.currentCourt < currentPosition)
    .sort((a, b) => a.currentCourt - b.currentCourt);

  const estimatedRounds = Math.ceil(gamesNeeded / 2); // Rough estimate

  return {
    team,
    currentPosition,
    gamesNeeded,
    opponentsToBeat: teamsAbove.slice(0, gamesNeeded),
    estimatedRounds,
  };
};

// Generate QR code URL for live standings
export const generateQRCodeUrl = (tournamentId: string): string => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/tournament/${tournamentId}/standings`;
};

// Move teams based on ladder rules
export const moveTeams = (teams: Team[], courts: number): Team[] => {
  return teams.map((team) => {
    const currentCourt = team.currentCourt;
    let newCourt = currentCourt;
    let movement = 0;

    // Find the team's match result to determine movement
    // This would be implemented based on the current round's results
    // For now, we'll assume the movement is calculated elsewhere

    // Winners move up (lower court number), losers move down (higher court number)
    // Court 1 winners stay, Court 8 losers stay

    return {
      ...team,
      currentCourt: newCourt,
      totalMovement: team.totalMovement + movement,
    };
  });
};

// Create initial round with random team placement
export const createInitialRound = (teams: Team[], courts: number): Round => {
  const shuffledTeams = [...teams].sort(() => Math.random() - 0.5);
  const matches: Match[] = [];

  // Handle odd number of teams with bye
  const teamsWithBye =
    shuffledTeams.length % 2 === 1 ? [...shuffledTeams, null] : shuffledTeams;

  // Calculate how many matches we can create
  const maxMatches = Math.floor(teamsWithBye.length / 2);
  const matchesToCreate = Math.min(courts, maxMatches);

  for (let i = 0; i < matchesToCreate; i++) {
    const team1 = teamsWithBye[i * 2];
    const team2 = teamsWithBye[i * 2 + 1];

    if (team1 && team2) {
      const match: Match = {
        id: generateId(),
        round: 1,
        court: i + 1,
        team1: { ...team1, currentCourt: i + 1, startingCourt: i + 1 },
        team2: { ...team2, currentCourt: i + 1, startingCourt: i + 1 },
        result: null,
        isBye: false,
        isMatchOfTheRound: false,
      };
      matches.push(match);
    } else if (team1) {
      // Bye match
      const match: Match = {
        id: generateId(),
        round: 1,
        court: i + 1,
        team1: { ...team1, currentCourt: i + 1, startingCourt: i + 1 },
        team2: { ...team1, currentCourt: i + 1, startingCourt: i + 1 }, // Placeholder
        result: null,
        isBye: true,
        isMatchOfTheRound: false,
      };
      matches.push(match);
    }
  }

  // If we have more courts than matches, create empty court displays
  for (let i = matchesToCreate; i < courts; i++) {
    const match: Match = {
      id: generateId(),
      round: 1,
      court: i + 1,
      team1: {
        id: `empty-${i + 1}`,
        name: "Empty Court",
        player1: "",
        player2: "",
        wins: 0,
        losses: 0,
        currentCourt: i + 1,
        startingCourt: i + 1,
        totalMovement: 0,
        achievements: [],
        court1Streak: 0,
        perfectGames: 0,
      },
      team2: {
        id: `empty-${i + 1}-2`,
        name: "Empty Court",
        player1: "",
        player2: "",
        wins: 0,
        losses: 0,
        currentCourt: i + 1,
        startingCourt: i + 1,
        totalMovement: 0,
        achievements: [],
        court1Streak: 0,
        perfectGames: 0,
      },
      result: null,
      isBye: true,
      isMatchOfTheRound: false,
    };
    matches.push(match);
  }

  const matchOfTheRound = findMatchOfTheRound(matches);

  return {
    roundNumber: 1,
    matches,
    completed: false,
    matchOfTheRound,
  };
};

// Create next round based on previous round results
export const createNextRound = (
  teams: Team[],
  previousRound: Round,
  courts: number
): Round => {
  // Update team positions based on previous round results
  const updatedTeams = teams.map((team) => {
    const match = previousRound.matches.find(
      (m) => m.team1.id === team.id || m.team2.id === team.id
    );

    if (!match || !match.result) return team;

    const isWinner = match.result.winner === team.id;
    const currentCourt = team.currentCourt;

    if (isWinner && currentCourt > 1) {
      return {
        ...team,
        currentCourt: currentCourt - 1,
        totalMovement: team.totalMovement - 1,
      };
    } else if (!isWinner && currentCourt < courts) {
      return {
        ...team,
        currentCourt: currentCourt + 1,
        totalMovement: team.totalMovement + 1,
      };
    }

    return team;
  });

  // Create new matches based on updated positions
  const courtAssignments: { [key: number]: Team[] } = {};

  updatedTeams.forEach((team) => {
    if (!courtAssignments[team.currentCourt]) {
      courtAssignments[team.currentCourt] = [];
    }
    courtAssignments[team.currentCourt].push(team);
  });

  const matches: Match[] = [];
  const roundNumber = previousRound.roundNumber + 1;

  Object.entries(courtAssignments).forEach(([courtStr, teamsOnCourt]) => {
    const court = parseInt(courtStr);

    if (teamsOnCourt.length >= 2) {
      // Create match with first two teams
      const match: Match = {
        id: generateId(),
        round: roundNumber,
        court,
        team1: teamsOnCourt[0],
        team2: teamsOnCourt[1],
        result: null,
        isBye: false,
        isMatchOfTheRound: false,
      };
      matches.push(match);

      // Handle remaining teams (if any) - they get a bye
      for (let i = 2; i < teamsOnCourt.length; i++) {
        const byeMatch: Match = {
          id: generateId(),
          round: roundNumber,
          court: court + i - 1,
          team1: teamsOnCourt[i],
          team2: teamsOnCourt[i], // Placeholder for bye
          result: null,
          isBye: true,
          isMatchOfTheRound: false,
        };
        matches.push(byeMatch);
      }
    } else if (teamsOnCourt.length === 1) {
      // Single team gets a bye
      const byeMatch: Match = {
        id: generateId(),
        round: roundNumber,
        court,
        team1: teamsOnCourt[0],
        team2: teamsOnCourt[0], // Placeholder for bye
        result: null,
        isBye: true,
        isMatchOfTheRound: false,
      };
      matches.push(byeMatch);
    }
  });

  const matchOfTheRound = findMatchOfTheRound(matches);

  return {
    roundNumber,
    matches,
    completed: false,
    matchOfTheRound,
  };
};

// Calculate tournament statistics
export const calculateTournamentStats = (
  tournament: Tournament
): TournamentStats => {
  const teams = tournament.teams;

  // Find champion (team on court 1 at the end)
  const champion = teams.find((team) => team.currentCourt === 1) || null;

  // Find team with most wins
  const mostWins = teams.reduce(
    (max, team) => (team.wins > max.wins ? team : max),
    teams[0]
  );

  // Find team with biggest climb (most negative movement)
  const biggestClimb = teams.reduce(
    (max, team) => (team.totalMovement < max.totalMovement ? team : max),
    teams[0]
  );

  // Calculate total games played
  const totalGamesPlayed = tournament.rounds.reduce(
    (total, round) =>
      total + round.matches.filter((match) => match.result?.completed).length,
    0
  );

  const averageGamesPerTeam =
    teams.length > 0 ? totalGamesPlayed / teams.length : 0;

  // Find court 1 defender
  const court1Defender = teams.find((team) => team.currentCourt === 1) || null;
  const court1DefenderStreak = court1Defender?.court1Streak || 0;

  // Calculate total perfect games
  const perfectGames = teams.reduce(
    (total, team) => total + team.perfectGames,
    0
  );

  // Collect all achievements
  const allAchievements = teams.flatMap((team) => team.achievements);

  return {
    champion,
    mostWins,
    biggestClimb,
    totalGamesPlayed,
    averageGamesPerTeam,
    court1Defender,
    court1DefenderStreak,
    perfectGames,
    achievements: allAchievements,
  };
};

// Validate score input
export const validateScore = (score: number): boolean => {
  return score >= 0 && score <= 13 && Number.isInteger(score);
};

// Check if a game is complete
export const isGameComplete = (
  team1Score: number,
  team2Score: number
): boolean => {
  const { winner } = determineWinner(team1Score, team2Score);
  return winner !== 0;
};
